// DE-KPIS-LIVE (docs/design/2026-10-31-de-kpis-live.md): KPIs › DE KPIs, the company view. Every
// digital employee the viewer may see, one row each, with its activity, operating KPIs and cost
// over the chosen weeks — runs (done, failed, stopped), success, cost, per run, per outcome, spend
// vs budget, memory — and its helpers (AG) grouped under it with what their calls cost. Each row
// reads the DE's Record (`digitalEmployees.record`, the drawer's and the DE page's source), so the
// three views never disagree; the line above sums the rows shown. A DE the viewer may not read
// says so in its row. No data: "—" and an empty state, never a sample.
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  InfoTip,
  LocationHeader,
  Segmented,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
} from "@companyos/ui";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BotIcon } from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../api/client";
import type { DeRecord } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { budgetLine, money, outcomeFigure } from "../de/de-ops";
import { EvidenceDialog, formatRate } from "../de/record-tab";
import { formatMoney } from "../format";
import { deRecordQuery, digitalEmployeesQuery, memoryGateQuery, memoryUseQuery } from "../queries";
import { LoadError, Loading, LoadMore, Nothing } from "../screen-parts";

const WINDOWS = ["1", "4", "12"] as const;
type Window = (typeof WINDOWS)[number];

type Summed = { runs: number; done: number; cost: number; currency: string | null };

/** The rows' sum (the DEs whose record loaded, in one currency). */
export function sumRecords(records: readonly DeRecord[]): Summed {
  const out: Summed = { runs: 0, done: 0, cost: 0, currency: null };
  for (const r of records) {
    if (out.currency !== null && out.currency !== r.currency) continue;
    out.currency = r.currency;
    out.runs += r.total.runs;
    out.done += r.total.runs_done;
    out.cost += r.total.cost_minor;
  }
  return out;
}

/** The record is refused to this viewer (403) or hidden from them (404): an expected case. */
export function isRestricted(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

function Head({ label, info }: { label: string; info?: string }) {
  return (
    <TableHead scope="col" className="text-right whitespace-nowrap">
      <span className="inline-flex items-center gap-1">
        {label}
        {info ? <InfoTip label={`About ${label}`}>{info}</InfoTip> : null}
      </span>
    </TableHead>
  );
}

export function DeKpisScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const [span, setSpan] = useState<Window>("4");
  const weeks = Number(span);
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const list = (des.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (d) => d.status !== "retired" && d.removed_at === null,
  );
  const records = useQueries({
    queries: list.map((d) => ({
      ...deRecordQuery(api, company.id, d.membership_id, weeks),
      retry: false,
      // A DE the viewer may list but not read the record of answers 404 (403 for a narrower
      // scope): its row says "Restricted", with no toast.
      meta: { quietStatuses: [403, 404] },
    })),
  });
  const loaded = records.flatMap((q) => (q.data ? [q.data] : []));
  const sum = sumRecords(loaded);
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="de-kpis">
      <LocationHeader
        icon={BotIcon}
        title="DE KPIs"
        badge={
          <InfoTip label="About DE KPIs">
            Each digital employee's runs, KPIs and cost, fed from its own work and the cost ledger.
            Its helpers (AG) are listed under it. Open a DE for its full record.
          </InfoTip>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto px-6 pt-4 pb-12 max-md:px-4">
        <div className="mx-auto flex max-w-6xl flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground tabular-nums" data-slot="de-kpis-sum">
              {loaded.length === 0 || sum.currency === null
                ? "—"
                : `${sum.runs} runs · ${formatRate(sum.runs > 0 ? sum.done / sum.runs : null)} done · ${formatMoney(sum.cost, sum.currency)}`}
            </p>
            <Segmented
              aria-label="Weeks shown"
              size="sm"
              value={span}
              onValueChange={setSpan}
              options={WINDOWS.map((w) => ({
                value: w,
                label: w === "1" ? "This week" : `${w} wks`,
              }))}
            />
          </div>
          {loaded.length > 0 ? (
            <DecisionLedger
              api={api}
              company={company}
              rows={list.flatMap((d, i) => {
                const r = records[i]?.data;
                return r ? [{ id: d.membership_id, name: d.display_name, record: r }] : [];
              })}
            />
          ) : null}
          <MemoryUsePanel api={api} company={company} days={weeks * 7} />
          <MemoryGatePanel api={api} company={company} />
          {des.isPending ? (
            <Loading label="Loading digital employees" rows={4} />
          ) : des.isError ? (
            <LoadError what="Digital employees" error={des.error} />
          ) : list.length === 0 ? (
            <Nothing title="No digital employees">Hire one to see its KPIs here.</Nothing>
          ) : (
            <div className="min-w-0 overflow-x-auto">
              <Table aria-label="Digital employees' KPIs">
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Who</TableHead>
                    <TableHead scope="col" className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        Outcomes
                        <InfoTip label="About Outcomes">
                          The outcome KPIs of each DE's role, editable on its seat. Counted from
                          what CompanyOS sees happen; one only the DE can report reads "not tracked"
                          until it does.
                        </InfoTip>
                      </span>
                    </TableHead>
                    <Head
                      label="Runs"
                      info="Runs that ended: done · failed or blocked · stopped early. For a helper (AG): its jobs, each attempt it ran at the DE's helper cards that ended: done · blocked or failed."
                    />
                    <Head
                      label="Success"
                      info="Runs done, out of all runs that ended. For a helper: jobs done, out of those done or blocked."
                    />
                    <Head
                      label="Cost"
                      info="From the cost ledger, its helpers' calls included. Under it: per run, and per outcome (over work outcomes: tasks completed and outputs approved)."
                    />
                    <Head
                      label="Budget"
                      info="Spend against its budget for the budget's own period."
                    />
                    <Head
                      label="Memory"
                      info="Reads (brief, resume, procedure handed to its runs) · saves its runs asked for."
                    />
                    <Head label="Decisions" info={DECISIONS_INFO} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((d, i) => {
                    const q = records[i];
                    return (
                      <DeRows
                        key={d.membership_id}
                        slug={company.slug}
                        id={d.membership_id}
                        name={d.display_name}
                        seat={d.seat?.title ?? null}
                        record={q?.data ?? null}
                        error={q?.error ?? null}
                        pending={q?.isPending ?? true}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <LoadMore
            what="digital employees"
            hasMore={des.hasNextPage}
            loading={des.isFetchingNextPage}
            onMore={() => void des.fetchNextPage()}
          />
        </div>
      </div>
    </div>
  );
}

type MemoryGate = OperationOutput<"kpis.memoryGate">;
type GateMetric = MemoryGate["weeks"][number]["metrics"][number];

/** MEMORY-FIT: each gate metric's name on the page. */
export const GATE_LABELS: Record<GateMetric["key"], string> = {
  use_share: "Use share",
  briefs_per_run: "Briefs per run",
  brief_tokens: "Brief tokens (median · p95)",
  precedents_cited: "Precedents cited",
  repeat_questions: "Repeat questions",
  duplicate_rate: "Duplicate rate · restatements",
  writes_per_run: "Writes per run (most · mean)",
  eval: "Eval: memory on vs off (points · cost)",
};

const SHARE_KEYS = new Set<GateMetric["key"]>([
  "use_share",
  "precedents_cited",
  "repeat_questions",
  "duplicate_rate",
]);

/** MEMORY-FIT: a gate metric's value as the page shows it (pure). */
export function gateValue(m: GateMetric): string {
  if (m.value === null) return "—";
  const main = SHARE_KEYS.has(m.key)
    ? formatRate(m.value)
    : m.key === "eval"
      ? `${m.value >= 0 ? "+" : ""}${m.value}`
      : String(m.value);
  if (m.value2 === null) return main;
  const second = m.key === "eval" ? formatRate(m.value2) : String(m.value2);
  return `${main} · ${second}`;
}

/** MEMORY-FIT: memory's release gate, the last two weeks (hidden while its flag is off). */
function MemoryGatePanel({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery({ ...memoryGateQuery(api, company.id), retry: false });
  const gate = q.data;
  if (!gate?.enabled) return null;
  const [latest, prior] = gate.weeks;
  if (!latest) return null;
  return (
    <section
      aria-label="Memory release gate"
      className="rounded-lg border border-border p-4"
      data-slot="memory-gate"
    >
      <h2 className="mb-1 inline-flex items-center gap-1 text-sm font-medium">
        Memory release gate
        <InfoTip label="About the memory release gate">
          The eight checks memory must pass, green two weeks running, before it is sold. A check
          with nothing to count reads "not measured". The eval needs +{gate.eval_lift_points}{" "}
          points.
        </InfoTip>
      </h2>
      <p className="mb-2 text-sm text-muted-foreground" data-slot="memory-gate-verdict">
        {gate.release_green ? "Green both weeks." : "Not green yet."}
      </p>
      {gate.detail ? <p className="mb-2 text-sm text-muted-foreground">{gate.detail}</p> : null}
      <div className="min-w-0 overflow-x-auto">
        <Table aria-label="Memory release gate metrics">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Check</TableHead>
              <TableHead scope="col" className="text-right">
                This week
              </TableHead>
              <TableHead scope="col" className="text-right">
                Last week
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {latest.metrics.map((m) => {
              const p = prior?.metrics.find((x) => x.key === m.key);
              return (
                <TableRow key={m.key}>
                  <TableHead scope="row" className="font-normal">
                    <span className="inline-flex items-center gap-1">
                      {GATE_LABELS[m.key]}
                      <InfoTip label={`About ${GATE_LABELS[m.key]}`}>{m.detail}</InfoTip>
                    </span>
                  </TableHead>
                  <TableCell className="text-right tabular-nums">
                    {`${gateValue(m)} (${m.status === "unmeasured" ? "not measured" : m.status})`}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p
                      ? `${gateValue(p)} (${p.status === "unmeasured" ? "not measured" : p.status})`
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

type MemoryUse = OperationOutput<"kpis.memoryUse">;
/** At most this many DEs get a column of their own in the by-day table. */
const MEMORY_USE_COLUMNS = 6;

const MEMORY_USE_INFO =
  "Of the records memory handed a digital employee during a run, the share that same run then read, cited or used. Counted by Recall per run, summed by day. A day with nothing handed shows —.";

/**
 * RECALL-USAGE: the rows of the by-day table, newest day first: the company's share and each
 * shown DE's share that day (null: nothing delivered to it that day).
 */
export function memoryUseRows(
  share: MemoryUse,
  agents: number = MEMORY_USE_COLUMNS,
): { day: string; company: number | null; agents: (number | null)[] }[] {
  const shown = share.agents.slice(0, agents);
  return [...share.company.by_day]
    .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
    .map((d) => ({
      day: d.day,
      company: d.use_share,
      agents: shown.map((a) => a.by_day.find((x) => x.day === d.day)?.use_share ?? null),
    }));
}

/** RECALL-USAGE: the memory use share, company-wide and per DE, by day (hidden while off). */
function MemoryUsePanel({
  api,
  company,
  days,
}: {
  api: Api;
  company: CompanyAccess;
  days: number;
}) {
  const q = useQuery({ ...memoryUseQuery(api, company.id, days), retry: false });
  const share = q.data;
  if (!share?.enabled) return null;
  const shown = share.agents.slice(0, MEMORY_USE_COLUMNS);
  const rows = memoryUseRows(share);
  const cell = "text-right tabular-nums";
  return (
    <section
      aria-label="Memory use share"
      className="rounded-lg border border-border p-4"
      data-slot="memory-use"
    >
      <h2 className="mb-1 inline-flex items-center gap-1 text-sm font-medium">
        Memory use share
        <InfoTip label="About the memory use share">{MEMORY_USE_INFO}</InfoTip>
      </h2>
      <p className="mb-2 text-sm text-muted-foreground tabular-nums" data-slot="memory-use-total">
        {`${formatRate(share.company.use_share)} company-wide · ${share.company.records_used} of ${share.company.records_delivered} records used`}
      </p>
      {share.detail ? <p className="mb-2 text-sm text-muted-foreground">{share.detail}</p> : null}
      {share.agents.length > 0 ? (
        <ul
          className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-sm"
          aria-label="Per digital employee"
        >
          {share.agents.map((a) => (
            <li key={a.membership_id} className="tabular-nums">
              {a.display_name}: {formatRate(a.use_share)}
            </li>
          ))}
        </ul>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No runs were handed memory in these weeks.</p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table aria-label="Memory use share by day">
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Day</TableHead>
                <TableHead scope="col" className="text-right">
                  Company
                </TableHead>
                {shown.map((a) => (
                  <TableHead key={a.membership_id} scope="col" className="text-right">
                    {a.display_name}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.day}>
                  <TableCell>{r.day}</TableCell>
                  <TableCell className={cell}>{formatRate(r.company)}</TableCell>
                  {r.agents.map((v, i) => (
                    <TableCell key={shown[i]?.membership_id ?? i} className={cell}>
                      {formatRate(v)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

const DECISIONS_INFO =
  "Decided in its run · escalated to a person · reversed. Decided: actions the agent policy let it take on its own. Escalated: approvals it asked for, premises and handoffs it raised. Reversed: its work undone or thrown out as wrong (an undo of its write, a send-back marked wrong); an edit is not a reversal.";

/** DECISION-LEDGER: one week's three counters, summed over the DEs shown. */
export type WeekDecisions = {
  week: string;
  starts_on: string;
  decided: number;
  escalated: number;
  reversed: number;
};

/** The weekly rollup of the records' decision counters, oldest week first. */
export function weeklyDecisions(records: readonly DeRecord[]): WeekDecisions[] {
  const by = new Map<string, WeekDecisions>();
  for (const r of records)
    for (const w of r.weeks) {
      const x = by.get(w.week) ?? {
        week: w.week,
        starts_on: w.starts_on,
        decided: 0,
        escalated: 0,
        reversed: 0,
      };
      x.decided += w.decided;
      x.escalated += w.escalated;
      x.reversed += w.reversed;
      by.set(w.week, x);
    }
  return [...by.values()].sort((a, b) => (a.week < b.week ? -1 : a.week > b.week ? 1 : 0));
}

type LedgerRow = { id: string; name: string; record: DeRecord };

/**
 * DECISION-LEDGER (roadmap Phase 1 item 1): decided / escalated / reversed per week over the DEs
 * shown, and each reversal week per DE, which opens who reversed what and why (the record's
 * evidence). Every undo of an agent's write is listed here; none is hidden.
 */
function DecisionLedger({
  api,
  company,
  rows,
}: {
  api: Api;
  company: CompanyAccess;
  rows: readonly LedgerRow[];
}) {
  const [open, setOpen] = useState<{ id: string; week: string } | null>(null);
  const weeks = weeklyDecisions(rows.map((r) => r.record));
  const total = weeks.reduce(
    (a, w) => ({
      decided: a.decided + w.decided,
      escalated: a.escalated + w.escalated,
      reversed: a.reversed + w.reversed,
    }),
    { decided: 0, escalated: 0, reversed: 0 },
  );
  const reversals = rows.flatMap((r) =>
    r.record.weeks
      .filter((w) => w.reversed > 0)
      .map((w) => ({ id: r.id, name: r.name, week: w.week, from: w.starts_on, n: w.reversed })),
  );
  const cell = "text-right tabular-nums";
  return (
    <section
      aria-label="Decision ledger"
      className="rounded-lg border border-border p-4"
      data-slot="decision-ledger"
    >
      <h2 className="mb-2 inline-flex items-center gap-1 text-sm font-medium">
        Decision ledger
        <InfoTip label="About the decision ledger">{DECISIONS_INFO}</InfoTip>
      </h2>
      <Table aria-label="Decisions per week">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Week of</TableHead>
            <TableHead scope="col" className="text-right">
              Decided
            </TableHead>
            <TableHead scope="col" className="text-right">
              Escalated
            </TableHead>
            <TableHead scope="col" className="text-right">
              Reversed
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {weeks.map((w) => (
            <TableRow key={w.week} data-week={w.week}>
              <TableHead scope="row" className="font-normal tabular-nums">
                {w.starts_on}
              </TableHead>
              <TableCell className={cell}>{w.decided}</TableCell>
              <TableCell className={cell}>{w.escalated}</TableCell>
              <TableCell className={cell}>{w.reversed}</TableCell>
            </TableRow>
          ))}
          {weeks.length > 1 ? (
            <TableRow data-week="total">
              <TableHead scope="row">Total</TableHead>
              <TableCell className={`${cell} font-medium`}>{total.decided}</TableCell>
              <TableCell className={`${cell} font-medium`}>{total.escalated}</TableCell>
              <TableCell className={`${cell} font-medium`}>{total.reversed}</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {reversals.length > 0 ? (
        <ul aria-label="Reversals" className="mt-3 flex flex-col gap-1 text-sm">
          {reversals.map((x) => (
            <li key={`${x.id}:${x.week}`} data-reversal={`${x.id}:${x.week}`}>
              <button
                type="button"
                className="text-primary-text underline-offset-4 hover:underline"
                onClick={() => setOpen({ id: x.id, week: x.week })}
              >
                {x.name} · week of {x.from}: {x.n} reversed — who and why
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open ? (
        <EvidenceDialog
          api={api}
          company={company}
          id={open.id}
          week={open.week}
          signal="reversed"
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}

function DeRows({
  slug,
  id,
  name,
  seat,
  record,
  error,
  pending,
}: {
  slug: string;
  id: string;
  name: string;
  seat: string | null;
  record: DeRecord | null;
  error: unknown;
  pending: boolean;
}) {
  const who = (
    <TableHead scope="row" className="font-normal">
      <span className="flex min-w-0 items-center gap-2">
        <WorkerBadge kind="de" />
        <span className="flex min-w-0 flex-col">
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug, id }}
            search={(prev: Record<string, unknown>) => ({
              ...keepFocus(prev),
              tab: "record" as const,
            })}
            className="truncate font-medium text-primary-text hover:underline"
          >
            {name}
          </Link>
          {seat ? <span className="truncate text-xs text-muted-foreground">{seat}</span> : null}
        </span>
      </span>
    </TableHead>
  );
  if (!record)
    return (
      <TableRow data-de={id}>
        {who}
        <TableCell colSpan={7} className="text-sm text-muted-foreground">
          {pending ? "Loading…" : isRestricted(error) ? "Restricted" : "Could not load its record."}
        </TableCell>
      </TableRow>
    );
  const t = record.total;
  const c = record.currency;
  const budget = budgetLine(record);
  const cell = "text-right tabular-nums whitespace-nowrap";
  return (
    <Fragment>
      <TableRow data-de={id}>
        {who}
        <TableCell className="w-52 max-w-52 text-xs" data-field="outcomes">
          {record.outcomes.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {record.outcomes.map((o) => (
                <li key={o.key} className="flex justify-between gap-2" data-outcome={o.key}>
                  <span className="truncate text-muted-foreground" title={o.label}>
                    {o.label}
                  </span>
                  <span className="whitespace-nowrap tabular-nums">
                    {o.tracked ? (
                      outcomeFigure(o.value, o.unit, c)
                    ) : (
                      <span className="text-faint">not tracked</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </TableCell>
        <TableCell className={cell} data-field="runs">
          <span className="font-medium">{t.runs}</span>
          <span className="block text-xs text-muted-foreground">
            {t.runs_done} · {t.runs_failed} · {t.runs_stopped}
          </span>
          {t.runs_budget_stopped > 0 && (
            <span className="block text-xs text-muted-foreground" data-field="budget_stopped">
              {t.runs_budget_stopped} stopped by budget
            </span>
          )}
        </TableCell>
        <TableCell className={cell} data-field="success">
          {formatRate(t.run_success_rate)}
        </TableCell>
        <TableCell className={cell} data-field="cost">
          {money(t.cost_minor, c)}
          <span className="block text-xs text-muted-foreground" data-field="per_run">
            {money(t.cost_per_run_minor, c)} / run
          </span>
          <span className="block text-xs text-muted-foreground" data-field="per_outcome">
            {money(t.cost_per_outcome_minor, c)} / outcome
          </span>
        </TableCell>
        <TableCell className={cell} data-field="budget">
          {budget.value}
          {budget.sub ? (
            <span className="block text-xs text-muted-foreground">{budget.sub}</span>
          ) : null}
        </TableCell>
        <TableCell className={cell} data-field="memory">
          {record.memory.source === null ? (
            <span className="text-muted-foreground">Off</span>
          ) : (
            `${t.memory_reads} · ${t.memory_saves}`
          )}
        </TableCell>
        <TableCell className={cell} data-field="decisions">
          {t.decided} · {t.escalated} · {t.reversed}
        </TableCell>
      </TableRow>
      {record.helpers.map((h) => (
        <TableRow key={h.id} data-helper={h.slug} data-under={id} className="bg-muted/30">
          <TableHead scope="row" className="font-normal">
            <span className="flex min-w-0 items-center gap-2 pl-6">
              <WorkerBadge kind="ag" />
              <span className="truncate text-sm">
                {h.name}
                {h.relation === "uses" ? <span className="text-faint"> · shared</span> : null}
              </span>
            </span>
          </TableHead>
          <TableCell />
          <TableCell className={cell} data-field="helper-jobs">
            {h.jobs > 0 ? (
              <>
                <span className="font-medium">{h.jobs}</span>
                <span className="block text-xs text-muted-foreground">
                  {h.jobs_done} · {h.jobs_blocked}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
          <TableCell className={cell} data-field="helper-success">
            {formatRate(h.job_success_rate)}
          </TableCell>
          <TableCell className={cell} data-field="helper-cost">
            {h.calls > 0 ? (
              money(h.cost_minor, c)
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
            {h.calls > 0 ? (
              <span className="block text-xs text-muted-foreground">{h.calls} calls</span>
            ) : null}
          </TableCell>
          <TableCell colSpan={3} className="text-xs text-muted-foreground">
            {h.status === "active"
              ? h.calls > 0 || h.jobs > 0
                ? ""
                : "No jobs or calls"
              : h.status.replace("_", " ")}
          </TableCell>
        </TableRow>
      ))}
    </Fragment>
  );
}
